import {Link as RouterLink} from 'react-router-dom';
import {Link} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {KYC_STATUS_LABELS, KycRecord, useGetKyc} from '@entities/kyc';
import {MrTable} from '@shared/components';
import {
  DATETIME_FORMAT,
  entityTableCommonProps,
  ROUTE__USER_ACCOUNT,
} from '@shared/constants';
import {useUrlState} from '@shared/hooks';
import {formatInTz} from '@shared/utils';
import {Header} from '@widgets/common';

import {PaginationState} from '@/shared/types';

const localStorageKey = 'kycTable';

type UrlState = PaginationState;

const columns: MRT_ColumnDef<KycRecord>[] = [
  {
    accessorKey: 'accountId',
    header: 'Account ID',
    size: 140,
    Cell: ({row}) => {
      const {accountId} = row.original;

      return (
        <Link
          component={RouterLink}
          to={ROUTE__USER_ACCOUNT.replace(':id', accountId)}
        >
          {accountId}
        </Link>
      );
    },
  },
  {
    accessorKey: 'provider',
    header: 'KYC provider',
    size: 180,
  },
  {
    accessorKey: 'registeredAt',
    header: 'Registration date and time',
    size: 180,
    Cell: ({row}) => formatInTz(row.original.registeredAt, DATETIME_FORMAT),
  },
  {
    accessorKey: 'country',
    header: 'Country',
    size: 160,
  },
  {
    accessorKey: 'status',
    header: 'KYC status',
    size: 180,
    Cell: ({row}) => KYC_STATUS_LABELS[row.original.status],
  },
  {
    accessorKey: 'declineReason',
    header: 'Reason',
    size: 260,
    grow: true,
    Cell: ({row}) => row.original.declineReason || '-',
  },
];

export const KycPage = () => {
  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const {
    data,
    isPending: isLoading,
    error,
  } = useGetKyc({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
  });

  return (
    <MrTable
      error={error}
      columns={columns}
      data={data?.items ?? []}
      renderTopToolbarCustomActions={() => {
        return <Header title="" isBackButtonShown={false} />;
      }}
      enableColumnOrdering
      enableColumnDragging={false}
      enableColumnPinning
      localStorageKeyForSettings={localStorageKey}
      enableHiding
      enablePagination
      manualPagination
      onPaginationChange={setUrl}
      rowCount={data?.total ?? 0}
      state={{
        isLoading,
        pagination: urlState,
      }}
      initialState={{
        columnPinning: {
          left: ['accountId'],
        },
      }}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
